import { HttpError } from "ra-core";

/** Bound the complete response, including a body that stalls after HTTP 200. */
export const createTimedFetch =
  (fetchImpl: typeof fetch = fetch, timeoutMs = 15000): typeof fetch =>
  async (input, init) => {
    const startedAt = Date.now();
    const requestUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    const endpoint = new URL(requestUrl).pathname;
    const controller = new AbortController();
    const upstream =
      init?.signal ?? (input instanceof Request ? input.signal : undefined);
    const abort = () => controller.abort(upstream?.reason);
    if (upstream?.aborted) abort();
    else upstream?.addEventListener("abort", abort, { once: true });
    const requestTimeout =
      init?.body instanceof Blob || init?.body instanceof FormData
        ? Math.max(timeoutMs, 120000)
        : timeoutMs;
    const timer = setTimeout(
      () => controller.abort(new Error("CRM_REQUEST_TIMEOUT")),
      requestTimeout,
    );
    try {
      const response = await fetchImpl(input, {
        ...init,
        signal: controller.signal,
      });
      const body =
        response.status === 204 ||
        response.status === 205 ||
        response.status === 304
          ? null
          : await response.arrayBuffer();
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs > 3000)
        console.warn("CRM network slow", {
          endpoint,
          elapsedMs,
          status: response.status,
        });
      return new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    } catch (error) {
      if (upstream?.aborted) throw error;
      console.warn("CRM network failed", {
        endpoint,
        elapsedMs: Date.now() - startedAt,
        timeout: controller.signal.aborted,
      });
      throw new HttpError(
        "Не удалось получить ответ сервера. Проверьте соединение. Если вы сохраняли данные, проверьте результат перед повторной отправкой.",
        503,
        {
          code: controller.signal.aborted ? "network_timeout" : "network_error",
        },
      );
    } finally {
      clearTimeout(timer);
      upstream?.removeEventListener("abort", abort);
    }
  };

export const timedFetch = createTimedFetch();
